import { useEffect, useRef, useState } from 'react';

const lessons = [
  {
    id: 1,
    title: '디제잉 레슨',
    subtitle: 'Djing LESSON',
    icon: 'ri-mic-line',
    desc: `프로 디제잉부터 직장인, 학생, 취미 디제잉까지
탄탄한 커리큘럼으로
음악을 좋아하시는 분이라면 
누구나 즐겁게 배울 수 있습니다.`,
    features: ['선곡과 믹싱', 'Effect', '믹스셋 녹음 및 실습'],
    bg: 'bg-studio-card',
    accent: 'border-studio-gray',
  },
  {
    id: 2,
    title: '미디 작곡 레슨',
    subtitle: 'MIDI COMPOSITION',
    icon: 'ri-keyboard-line',
    desc: 'DAW부터 믹싱까지, 자신만의 비트와 멜로디를 만들 수 있는 완전한 작곡 스킬을 익힙니다.',
    features: ['DAW 기초 (Logic, Ableton)', '비트 메이킹', '멜로디 & 코드 진행', '믹싱 & 마스터링'],
    bg: 'bg-studio-surface',
    accent: 'border-studio-muted',
  },
  {
    id: 3,
    title: '랩 레슨',
    subtitle: 'Rap LESSON',
    icon: 'ri-mic-2-line',
    desc: '플로우부터 라이팅까지, 자신만의 랩을 완성하는 과정을 배웁니다. 기초 리듬감부터 곡 작업까지 체계적으로 학습합니다.',
    features: ['플로우 & 딜리버리', '라이팅 & 작사', '발음 & 발성', '곡 녹음 & 피드백'],
    bg: 'bg-studio-card',
    accent: 'border-studio-gray',
  },
  {
    id: 4,
    title: '프로듀싱 레슨',
    subtitle: 'Producing LESSON',
    icon: 'ri-voiceprint-line',
    desc: `랩, 보컬, 작곡의 아티스트 커리큘럼으로 프로듀싱의 기초부터 발매까지의 관리를 채계적으로 학습합니다.`,
    features: ['호흡 & 발성 기초', '탑 메이킹 & 작사, 작곡', '기초 플러그인 사용법, 사운드 디자인', '믹싱 & 마스터링'],
    bg: 'bg-studio-surface',
    accent: 'border-studio-muted',
  },
];

export default function LessonTypesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="lessons"
      ref={sectionRef}
      className="bg-studio-black py-24 md:py-32 px-6 md:px-12 lg:px-16"
    >
      <div className="w-full">
        {/* Section header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-studio-gray/30 font-bebas text-[clamp(60px,10vw,140px)] leading-none block">
            01
          </span>
          <h2
            className="text-white font-bebas uppercase leading-none -mt-6 md:-mt-12"
            style={{ fontSize: 'clamp(40px, 6vw, 80px)', lineHeight: 1 }}
          >
            어떤걸 배우나요 ?
          </h2>
          <p className="text-studio-gray font-sans text-lg md:text-xl mt-4">
            디제잉 · 미디 작곡 · 랩 · 프로듀싱 — 네 가지 전문 레슨 프로그램
          </p>
        </div>

        {/* Lesson cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {lessons.map((lesson, i) => (
            <div
              key={lesson.id}
              className={`${lesson.bg} border ${lesson.accent} rounded-lg p-8 md:p-10 group hover:border-studio-silver transition-all duration-500 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center border border-studio-muted rounded-lg mb-6 group-hover:border-studio-silver transition-colors">
                <i className={`${lesson.icon} text-studio-silver text-2xl`} />
              </div>

              {/* Title */}
              <h3 className="text-white font-bebas text-3xl md:text-4xl uppercase mb-1">
                {lesson.title}
              </h3>
              <p className="text-studio-gray font-sans text-sm tracking-widest uppercase mb-6">
                {lesson.subtitle}
              </p>

              {/* Description */}
              <p className="text-studio-silver font-sans text-base leading-relaxed mb-8">
                {lesson.desc}
              </p>

              {/* Features list */}
              <ul className="space-y-3">
                {lesson.features.map((feat) => (
                  <li key={feat} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-studio-silver rounded-full flex-shrink-0" />
                    <span className="text-studio-light font-sans text-sm">{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}